import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crown,
  Check,
  X,
  Loader2,
  Globe,
  Sparkles,
  Zap,
  BookOpen,
  Brain,
  Shield,
  Users as UsersIcon,
  ChevronDown,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { checkAuth, authFetch } from '../utils/authUtils';
import { PAYMENTS_DISABLED, PAYMENTS_UNAVAILABLE_MESSAGE } from '../utils/paymentAvailability';
import GatewayPicker from '../components/GatewayPicker';
import WalletPayButton from '../components/WalletPayButton';
import NigeriaBankTransferModal from '../components/NigeriaBankTransferModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TESTIMONIALS = [
  {
    name: 'Chiamaka O.',
    role: 'Medical Student · Lagos',
    quote:
      'AI quizzes turned my textbooks into a game I actually crave. My retention doubled in three weeks.',
    avatar: 'CO',
    accent: 'from-amber-400 to-rose-400',
  },
  {
    name: 'Daniel K.',
    role: 'Product Designer · Nairobi',
    quote:
      'Cinematic books on my commute + speed-reading drills = I finish a book every 10 days now.',
    avatar: 'DK',
    accent: 'from-indigo-400 to-purple-400',
  },
  {
    name: 'Sarah M.',
    role: 'Founder · London',
    quote:
      'The summaries alone pay for the subscription. I brief my team on new releases in minutes.',
    avatar: 'SM',
    accent: 'from-emerald-400 to-teal-400',
  },
];

const COMPARISON_ROWS = [
  { feature: 'Daily reading time', free: '20 min', premium: 'Unlimited' },
  { feature: 'Access window', free: '7 days / month', premium: 'Every day' },
  { feature: 'AI document summaries', free: false, premium: 'Short · Medium · Extended' },
  { feature: 'AI comprehension quizzes', free: false, premium: 'Unlimited' },
  // Feb 14 2026 — LEARN speed-reading suite: free readers get the
  // same 20 min/day × 7 days budget shared with reading time so the
  // paywall is consistent across every feature. Premium is unlimited.
  { feature: 'LEARN — 8 speed-reading drills', free: '20 min/day · 7 days', premium: 'Unlimited' },
  { feature: 'Cycle unlimited library passages into drills', free: false, premium: true },
  { feature: 'My Library — PDF/Word uploads', free: '1 day trial', premium: 'Unlimited (20 files)' },
  { feature: 'Full library (ebooks · audio · cinematic)', free: 'Limited', premium: 'Everything' },
  { feature: 'Offline reading', free: false, premium: true },
  { feature: 'Streak Shield perk', free: false, premium: true },
  { feature: 'Priority support', free: false, premium: true },
  { feature: 'Ads', free: 'Occasional', premium: 'Never' },
];

const FAQ = [
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel from your profile in two taps. You keep Premium until the current cycle ends — no questions asked.',
  },
  {
    q: 'What payment methods work?',
    a: 'Nigeria: Paystack (card, bank, USSD, Apple Pay). Everywhere else: Stripe (cards, Apple Pay, Google Pay, Link) — with PayPal available as a backup option.',
  },
  {
    q: 'Is there a free trial?',
    a: "Every new account is Free forever with a 20 min / day limit. You can also redeem a Premium Trial with coins from the shop.",
  },
  {
    q: 'Do I keep my progress if I downgrade?',
    a: 'Always. Your XP, coins, streaks, and library history are yours for life.',
  },
  {
    q: 'How secure is my data?',
    a: 'httpOnly session cookies, encrypted at rest, no password ever stored in plaintext, and zero ad-tracker scripts.',
  },
];

const Premium = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [region, setRegion] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' | 'yearly'
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [gateway, setGateway] = useState(null);
  const [bankTransferOpen, setBankTransferOpen] = useState(false);

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = async () => {
    try {
      const userData = await checkAuth();
      if (!userData) {
        navigate('/login');
        return;
      }
      setUser(userData);

      if ((localStorage.getItem('userRole') || '').toLowerCase() === 'admin') {
        navigate('/admin');
        return;
      }

      const response = await authFetch(`${BACKEND_URL}/api/subscription/detect-region`);
      const data = await response.json();
      setRegion(data.region);
      setPricing(data.pricing);
    } catch (error) {
      console.error('Failed to detect region:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthlyPrice = pricing?.plans?.[1];
  const yearlyPrice = pricing?.plans?.[12];
  const promoActive = !!pricing?.promo_active;
  const yearlyPromo = pricing?.plans_promo?.[12];
  const yearlyEffective = yearlyPromo ?? yearlyPrice;
  const yearlyMonthlyEquivalent =
    yearlyEffective && monthlyPrice ? yearlyEffective / 12 : null;
  const yearlySavePercent =
    yearlyEffective && monthlyPrice
      ? Math.max(0, Math.round((1 - yearlyEffective / (monthlyPrice * 12)) * 100))
      : 0;
  const promoDaysLeft = useMemo(() => {
    if (!pricing?.promo_ends_at) return null;
    const ms = new Date(pricing.promo_ends_at).getTime() - Date.now();
    if (Number.isNaN(ms) || ms <= 0) return null;
    return Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  }, [pricing?.promo_ends_at]);

  // Top Tier — yearly-only, premium-plus perks
  const topTierPrice = pricing?.top_tier?.plans?.[12];
  const topPerks = pricing?.perks?.premium_top || [];
  // Regional price strip — always show all three so users see fairness at a glance
  // (During launch promo, Nigeria yearly displays 12k striking through 15k, etc.)
  const regionalMatrix = [
    { key: 'first_world', flag: '🇬🇧', label: 'UK / First-world', sym: '£', premium: 18, premiumPromo: promoActive ? 15 : null, top: 36 },
    { key: 'nigeria',     flag: '🇳🇬', label: 'Nigeria',         sym: '₦', premium: 15000, premiumPromo: promoActive ? 12000 : null, top: 24000 },
    { key: 'africa',      flag: '🌍', label: 'Africa',           sym: '$', premium: 18, premiumPromo: promoActive ? 15 : null, top: 30 },
  ];

  const currentPlanMonths = billingCycle === 'yearly' ? 12 : 1;
  const currentPrice = pricing?.plans?.[currentPlanMonths];
  const currentPromoPrice = pricing?.plans_promo?.[currentPlanMonths];
  const currentEffectivePrice = currentPromoPrice ?? currentPrice;

  const handleSubscribe = async (months, planTier = 'premium', forceProvider = null) => {
    if (!user) {
      toast.error('Please log in first');
      window.location.href = '/login';
      return;
    }
    if (PAYMENTS_DISABLED) {
      toast.error(PAYMENTS_UNAVAILABLE_MESSAGE);
      return;
    }
    if (!region) {
      toast.error('Detecting your region… try again in a moment.');
      return;
    }
    // Feb 2026: Nigerian users pay only via the Moniepoint bank
    // transfer. Any Go-Yearly / Go-Monthly click opens the transfer
    // modal instead of hitting Stripe/PayPal.
    if (region === 'nigeria') {
      setSelectedPlan(`${planTier}:${months}`);
      setBankTransferOpen(true);
      return;
    }
    setProcessing(true);
    setSelectedPlan(`${planTier}:${months}`);

    // Parse JSON defensively — small guard for empty bodies
    const parseBody = async (response) => {
      try {
        const text = await response.text();
        if (!text) return {};
        try {
          return JSON.parse(text);
        } catch {
          return { detail: text };
        }
      } catch {
        return {};
      }
    };

    try {
      // Provider resolution:
      //   1. Caller-forced provider wins (e.g. the sticky CTA can
      //      pass `paystack` explicitly).
      //   2. User's picked gateway (via GatewayPicker) — may be null
      //      if they haven't scrolled far enough. Backend will fall
      //      back to the region's primary in that case (paystack for
      //      Nigeria, stripe for UK, paypal for others).
      const chosenProvider = forceProvider || gateway?.provider || null;
      const response = await authFetch(
        `${BACKEND_URL}/api/payments/subscription/checkout`,
        {
          method: 'POST',
          body: JSON.stringify({
            plan_months: months,
            plan_tier: planTier,
            origin_url: window.location.origin,
            region,
            provider: chosenProvider,
            is_diaspora_card: !!gateway?.is_diaspora_card,
          }),
        },
      );
      const data = await parseBody(response);
      if (!response.ok || !data.checkout_url) {
        // Backend explicitly redirects NG users to the bank transfer.
        const detail = data.detail;
        if (detail && typeof detail === 'object' && detail.reason === 'nigeria_bank_transfer_only') {
          setBankTransferOpen(true);
          setProcessing(false);
          setSelectedPlan(null);
          return;
        }
        const msg =
          (typeof detail === 'string' && detail) ||
          (detail && detail.message) ||
          data.message ||
          `Checkout could not be started (HTTP ${response.status})`;
        throw new Error(msg);
      }
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(`Payment could not start: ${error.message || error}`);
      setProcessing(false);
      setSelectedPlan(null);
    }
  };

  const regionLabel = useMemo(() => {
    if (region === 'nigeria') return 'Nigeria';
    if (region === 'kenya') return 'Kenya';
    if (region === 'africa') return 'Africa';
    return 'World';
  }, [region]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden text-white" data-testid="premium-page">
      {/* 3-month launch promo banner (Feb 2026) */}
      {promoActive && (
        <div
          className="relative z-20 bg-gradient-to-r from-emerald-600 via-emerald-500 to-amber-500 text-slate-900 text-center text-xs sm:text-sm font-bold py-2 px-4"
          data-testid="premium-promo-banner"
        >
          🎉 <span className="uppercase tracking-widest">Launch Promo</span> · 1-year plans are discounted for the next <span className="tabular-nums">{promoDaysLeft || 90}</span> days · save {yearlySavePercent}% with our best value plan
        </div>
      )}
      {/* Animated background orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-amber-500/20 blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[32rem] h-[32rem] rounded-full bg-fuchsia-500/20 blur-[140px]" />
        <div className="absolute bottom-0 left-1/4 w-[26rem] h-[26rem] rounded-full bg-indigo-500/20 blur-[120px]" />
      </div>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-300 mb-6">
          <Globe className="w-3.5 h-3.5 text-amber-300" />
          Region auto-detected · <span className="text-amber-300 font-semibold">{regionLabel}</span>
          <span className="ml-1 text-gray-500">·</span>
          <span className="text-gray-400">{pricing?.currency}</span>
        </div>

        <h2 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight">
          Unleash your mind.
          <br />
          <span className="bg-gradient-to-r from-amber-300 via-rose-300 to-fuchsia-300 bg-clip-text text-transparent">
            Learn without limits.
          </span>
        </h2>
        <p className="mt-6 text-lg text-gray-300 max-w-2xl mx-auto">
          Join thousands of learners turning every commute, coffee break and late-night hour into
          real progress. No caps. No ads. Just momentum.
        </p>

        {/* Social proof strip */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-8 text-xs uppercase tracking-widest text-gray-400">
          <Stat value="12,400+" label="Active learners" />
          <Stat value="1.2M" label="Pages read · last 30d" />
          <Stat value="98%" label="Would recommend" />
          <Stat value="4.9★" label="App rating" />
        </div>
      </section>

      {/* Billing toggle */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 flex justify-center mb-8">
        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-xl">
          {[
            { key: 'monthly', label: 'Monthly' },
            { key: 'yearly', label: 'Yearly', badge: yearlySavePercent > 0 ? `Save ${yearlySavePercent}%` : null },
          ].map((opt) => {
            const active = billingCycle === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setBillingCycle(opt.key)}
                data-testid={`billing-toggle-${opt.key}`}
                className={`relative px-5 py-2 text-sm font-semibold rounded-full transition-all ${
                  active
                    ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 shadow-lg shadow-amber-500/30'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {opt.label}
                {opt.badge && (
                  <span
                    className={`ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      active ? 'bg-slate-900 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'
                    }`}
                  >
                    {opt.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Pricing tiers */}
      <section
        id="pricing"
        className="relative z-10 max-w-6xl mx-auto px-4 pb-16 grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        {/* Free */}
        <TierCard
          title="Free"
          subtitle="For the curious"
          price="0"
          currency={pricing?.symbol || '$'}
          cycleLabel="forever"
          features={[
            '20 min of reading per day',
            '7 days of access per month',
            '20 min of LEARN drills per day (7 days)',
            'Core library access',
            'Streaks, coins & achievements',
          ]}
          disabledFeatures={['AI quizzes', 'AI summaries', 'Offline library', 'Unlimited LEARN']}
          ctaLabel="You're on Free"
          disabled
          testId="tier-free"
        />

        {/* Monthly */}
        <TierCard
          title="Monthly"
          subtitle="For steady learners"
          price={monthlyPrice}
          currency={pricing?.symbol || '$'}
          cycleLabel={`${pricing?.currency || 'USD'} / month`}
          features={[
            'Unlimited reading time',
            'Unlimited LEARN — all 8 speed-reading drills',
            'AI quizzes & summaries',
            'Full library + offline',
            'Priority support',
            'No ads, ever',
          ]}
          ctaLabel="Start Monthly"
          onClick={() => handleSubscribe(1, 'premium')}
          processing={processing && selectedPlan === 'premium:1'}
          highlighted={billingCycle === 'monthly'}
          testId="tier-monthly"
        />

        {/* Yearly */}
        <TierCard
          title="Yearly"
          subtitle="For serious growth"
          price={yearlyEffective}
          originalPrice={yearlyPromo ? yearlyPrice : null}
          currency={pricing?.symbol || '$'}
          cycleLabel={`${pricing?.currency || 'USD'} / year`}
          perMonthHint={
            yearlyMonthlyEquivalent
              ? `≈ ${pricing?.symbol}${yearlyMonthlyEquivalent.toFixed(0)} / month`
              : null
          }
          badge={yearlyPromo ? `LAUNCH · ${promoDaysLeft || 90}d left` : yearlySavePercent > 0 ? `SAVE ${yearlySavePercent}%` : 'BEST VALUE'}
          features={[
            'Everything in Monthly',
            `Save ${yearlySavePercent}% vs. monthly`,
            'Streak Shield included',
            'Exclusive yearly perks',
            'Lock in today\'s price',
          ]}
          ctaLabel="Go Yearly"
          onClick={() => handleSubscribe(12, 'premium')}
          processing={processing && selectedPlan === 'premium:12'}
          highlighted={billingCycle === 'yearly'}
          testId="tier-yearly"
        />
      </section>

      {/* Top Tier — yearly only, premium-plus perks */}
      {topTierPrice ? (
        <section
          id="top-tier"
          className="relative z-10 max-w-5xl mx-auto px-4 pb-16"
          data-testid="top-tier-section"
        >
          <div className="rounded-3xl overflow-hidden border border-purple-400/30 bg-gradient-to-br from-fuchsia-900/40 via-purple-900/30 to-indigo-900/40 backdrop-blur-xl p-6 md:p-10">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="max-w-xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white text-[11px] font-bold uppercase tracking-widest mb-3">
                  <Crown className="w-3.5 h-3.5" /> Top Tier
                </div>
                <h3 className="text-3xl md:text-4xl font-extrabold leading-tight">
                  Go all-in. One full year, every perk.
                </h3>
                <p className="text-gray-300 mt-2 text-sm md:text-base">
                  Top Tier unlocks concierge onboarding, offline library,
                  early access, cinematic 4K streams, a monthly reading-coach
                  session and 2× coin earn rate — on top of everything
                  included in Premium.
                </p>
              </div>

              <div className="text-right">
                <p className="text-[11px] uppercase tracking-widest text-purple-200/80 mb-1">
                  1-year all-inclusive
                </p>
                <div className="flex items-baseline gap-1 justify-end">
                  <span className="text-3xl text-purple-200">
                    {pricing?.top_tier?.symbol || pricing?.symbol || '£'}
                  </span>
                  <span
                    className="text-6xl md:text-7xl font-black text-white tabular-nums"
                    data-testid="top-tier-price"
                  >
                    {topTierPrice}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {pricing?.top_tier?.currency || pricing?.currency || 'GBP'} / year
                </p>
              </div>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 my-8">
              {topPerks.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-gray-200">
                  <Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <Button
              size="lg"
              onClick={() => handleSubscribe(12, 'premium_top')}
              disabled={processing}
              className="w-full md:w-auto bg-gradient-to-r from-fuchsia-500 via-purple-500 to-indigo-500 hover:from-fuchsia-600 hover:to-indigo-600 text-white font-bold px-8"
              data-testid="top-tier-cta"
            >
              {processing && selectedPlan === 'premium_top:12' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Unlock Top Tier · {pricing?.top_tier?.symbol || pricing?.symbol || '£'}
                  {topTierPrice}
                </>
              )}
            </Button>
          </div>

          {/* Regional pricing matrix — same perks worldwide, fair local prices */}
          <div
            className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl overflow-hidden"
            data-testid="top-tier-regional-matrix"
          >
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest font-semibold text-gray-400">
                Pricing around the world (Premium · Top Tier)
              </span>
              <span className="text-[10px] uppercase tracking-wider text-purple-300/80">
                Same perks · fair local prices
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
              {regionalMatrix.map((row) => {
                const active = region === row.key;
                return (
                  <div
                    key={row.key}
                    className={`p-5 ${active ? 'bg-purple-500/10' : ''}`}
                    data-testid={`regional-price-${row.key}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">{row.flag}</span>
                      <span className="text-sm font-semibold text-white">
                        {row.label}
                      </span>
                      {active && (
                        <span className="ml-auto text-[10px] uppercase tracking-wider bg-purple-500/30 text-purple-100 border border-purple-300/40 rounded-full px-2 py-0.5">
                          Your region
                        </span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-0.5">
                          Premium · yearly
                        </p>
                        <p className="text-lg font-bold text-white tabular-nums">
                          {row.premiumPromo != null && (
                            <span className="text-xs text-gray-500 line-through decoration-red-400/70 mr-1.5 font-normal">
                              {row.sym}{row.premium.toLocaleString()}
                            </span>
                          )}
                          {row.sym}
                          {(row.premiumPromo ?? row.premium).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-gray-500">/</div>
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-purple-300 mb-0.5">
                          Top Tier
                        </p>
                        <p className="text-lg font-bold text-purple-200 tabular-nums">
                          {row.sym}
                          {row.top.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {/* Feature comparison */}
      <section className="relative z-10 max-w-5xl mx-auto px-4 pb-16">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold">Every feature, side by side.</h3>
          <p className="text-gray-400 mt-2">No fine print. No surprises.</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden" data-testid="feature-comparison">
          <div className="grid grid-cols-3 text-sm font-semibold uppercase tracking-widest bg-white/5 border-b border-white/10">
            <div className="p-4 text-gray-400">Feature</div>
            <div className="p-4 text-gray-300 text-center">Free</div>
            <div className="p-4 text-center bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-200">
              Premium
            </div>
          </div>
          {COMPARISON_ROWS.map((row, i) => (
            <div
              key={row.feature}
              className={`grid grid-cols-3 text-sm ${
                i % 2 === 0 ? 'bg-white/[0.02]' : ''
              } border-b border-white/5 last:border-0`}
            >
              <div className="p-4 text-gray-300">{row.feature}</div>
              <div className="p-4 text-center text-gray-400">
                {row.free === true ? (
                  <Check className="inline w-4 h-4 text-emerald-400" />
                ) : row.free === false ? (
                  <X className="inline w-4 h-4 text-gray-600" />
                ) : (
                  row.free
                )}
              </div>
              <div className="p-4 text-center">
                {row.premium === true ? (
                  <Check className="inline w-5 h-5 text-amber-300" />
                ) : row.premium === false ? (
                  <X className="inline w-4 h-4 text-gray-600" />
                ) : (
                  <span className="text-amber-200 font-semibold">{row.premium}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 pb-16">
        <div className="text-center mb-10">
          <h3 className="text-3xl font-bold">Built for learners who refuse to plateau.</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <div
              key={t.name}
              className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl hover:bg-white/[0.07] transition-colors"
              data-testid={`testimonial-${t.avatar}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-10 h-10 rounded-full bg-gradient-to-br ${t.accent} flex items-center justify-center font-bold text-slate-900`}
                >
                  {t.avatar}
                </div>
                <div>
                  <p className="font-semibold">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.role}</p>
                </div>
              </div>
              <p className="text-gray-200 leading-relaxed">
                <span className="text-amber-300 text-xl leading-none mr-1">&ldquo;</span>
                {t.quote}
                <span className="text-amber-300 text-xl leading-none ml-1">&rdquo;</span>
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Value icons strip */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { Icon: Brain, label: 'AI-powered quizzes' },
            { Icon: BookOpen, label: 'Full library access' },
            { Icon: Zap, label: 'Unlimited time' },
            { Icon: Shield, label: 'Cancel anytime' },
          ].map(({ Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/30 to-orange-500/30 flex items-center justify-center">
                <Icon className="w-5 h-5 text-amber-300" />
              </div>
              <span className="text-sm font-medium">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 max-w-3xl mx-auto px-4 pb-24">
        <div className="text-center mb-10">
          <h3 className="text-3xl font-bold">Questions, answered.</h3>
        </div>
        <div className="space-y-2" data-testid="premium-faq">
          {FAQ.map((item, i) => {
            const open = openFaq === i;
            return (
              <div
                key={item.q}
                className={`rounded-xl border transition-colors ${
                  open
                    ? 'border-amber-500/40 bg-white/[0.06]'
                    : 'border-white/10 bg-white/[0.03]'
                }`}
              >
                <button
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-semibold">{item.q}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      open ? 'rotate-180 text-amber-300' : ''
                    }`}
                  />
                </button>
                {open && (
                  <div className="px-5 pb-4 text-sm text-gray-300 leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-4 pb-24 text-center">
        <div className="p-10 rounded-3xl bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-fuchsia-500/20 border border-amber-400/30 backdrop-blur-xl">
          <Sparkles className="w-8 h-8 text-amber-300 mx-auto mb-3" />
          <h3 className="text-3xl sm:text-4xl font-bold">
            Start your unlimited era today.
          </h3>
          <p className="text-gray-300 mt-3">
            Cancel anytime · No hidden fees · Keep your progress forever
          </p>
          <Button
            onClick={() => handleSubscribe(currentPlanMonths, 'premium')}
            disabled={processing}
            className="mt-6 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 font-bold text-base px-8 py-6 shadow-lg shadow-amber-500/30"
            data-testid="premium-final-cta"
          >
            {processing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                Go Premium {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} · {pricing?.symbol}
                {currentEffectivePrice}
                {currentPromoPrice && currentPromoPrice !== currentPrice && (
                  <span className="ml-2 text-xs opacity-70 line-through">
                    {pricing?.symbol}{Number(currentPrice).toLocaleString()}
                  </span>
                )}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>

          {/* Region-aware gateway picker — replaces the legacy "Stripe / or
              PayPal" fallback link. The chip the user taps becomes the
              `provider` we POST to /payments/subscription/checkout. */}
          <div className="mt-4 max-w-md mx-auto text-left" data-testid="premium-gateway-picker">
            {/* Native one-tap wallet button — Feb 12 2026 user spec.
                Renders only on Apple Pay/Google Pay-capable devices when
                STRIPE_PUBLISHABLE_KEY is configured. Falls back silently
                to the gateway picker below. */}
            <WalletPayButton
              packageId={billingCycle === 'yearly' ? 'premium_annual' : 'premium_monthly'}
              amount={(billingCycle === 'yearly' ? 4900 : 499)}
              currency="gbp"
              label={`Smarter Learning Hub · ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Premium`}
              onSuccess={() => {
                toast.success('Payment complete — Premium unlocked.');
                navigate('/premium/success');
              }}
              onError={(msg) => toast.error(msg || 'Wallet payment failed')}
            />
            {region === 'nigeria' ? (
              <div className="rounded-xl border-2 border-emerald-500/50 bg-gradient-to-r from-emerald-500/15 to-emerald-500/5 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 text-2xl">🇳🇬</div>
                  <div className="flex-1">
                    <p className="text-white font-bold text-sm">Nigerian subscribers pay by bank transfer</p>
                    <p className="text-[12px] text-slate-300 mt-1">
                      One-off Moniepoint transfer to <strong>Hallow Reflections Ltd</strong>. WhatsApp the receipt and we activate your subscription within a few hours.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setBankTransferOpen(true)}
                  className="mt-3 w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold py-2.5 flex items-center justify-center gap-2 transition-colors"
                  data-testid="premium-bank-transfer-cta"
                >
                  Pay by Bank Transfer <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <GatewayPicker
                onChange={setGateway}
                baseGbp={Number(currentEffectivePrice) || undefined}
              />
            )}
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA — sits above AppShell bottom bar */}
      <div className="fixed bottom-16 inset-x-0 z-40 md:hidden p-3 bg-black/70 backdrop-blur-xl border-t border-white/10">
        <Button
          onClick={() => handleSubscribe(currentPlanMonths, 'premium')}
          disabled={processing}
          className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 font-bold"
          data-testid="premium-sticky-cta"
        >
          {processing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Go {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} · {pricing?.symbol}
              {currentEffectivePrice}
            </>
          )}
        </Button>
      </div>

      {/* Nigeria-only manual bank transfer modal — mounts globally so
          any CTA can open it. Feb 2026. */}
      <NigeriaBankTransferModal
        open={bankTransferOpen}
        onClose={() => setBankTransferOpen(false)}
        planTier="premium"
        planMonths={currentPlanMonths}
        amountNgn={currentEffectivePrice}
        currencySymbol={pricing?.symbol || '₦'}
      />
    </div>
  );
};

const Stat = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <span className="text-xl font-bold text-white normal-case tracking-normal">
      {value}
    </span>
    <span className="text-[10px] text-gray-500">{label}</span>
  </div>
);

const TierCard = ({
  title,
  subtitle,
  price,
  originalPrice,
  currency,
  cycleLabel,
  perMonthHint,
  features = [],
  disabledFeatures = [],
  badge,
  ctaLabel,
  onClick,
  processing,
  highlighted,
  disabled,
  testId,
}) => {
  return (
    <div
      className={`relative rounded-3xl p-6 transition-all ${
        highlighted
          ? 'bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-fuchsia-500/10 border-2 border-amber-400/70 shadow-[0_0_60px_-20px_rgba(245,158,11,0.6)]'
          : 'bg-white/5 border border-white/10'
      } backdrop-blur-xl`}
      data-testid={testId}
    >
      {badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 shadow-lg">
          {badge}
        </span>
      )}
      <div className="mb-5">
        <h4 className="text-xl font-bold">{title}</h4>
        <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
      </div>
      <div className="mb-6">
        {originalPrice && originalPrice !== price ? (
          <div className="flex items-baseline gap-2 mb-1" data-testid={`${testId}-original-price`}>
            <span className="text-sm text-gray-500 line-through decoration-red-400/70 decoration-2">
              {currency}{typeof originalPrice === 'number' ? originalPrice.toLocaleString() : originalPrice}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-400/40">
              PROMO
            </span>
          </div>
        ) : null}
        <div className="flex items-baseline gap-1">
          <span className="text-2xl text-gray-400">{currency}</span>
          <span className="text-5xl font-extrabold tracking-tight">
            {typeof price === 'number' ? price.toLocaleString() : price}
          </span>
        </div>
        <p className="text-xs text-gray-400 mt-1">{cycleLabel}</p>
        {perMonthHint && (
          <p className="text-[11px] text-amber-300 mt-1 font-semibold">{perMonthHint}</p>
        )}
      </div>
      <Button
        onClick={onClick}
        disabled={disabled || processing}
        className={`w-full ${
          highlighted
            ? 'bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-slate-900 font-bold shadow-lg shadow-amber-500/30'
            : disabled
            ? 'bg-white/5 text-gray-500'
            : 'bg-white/10 hover:bg-white/20 text-white'
        }`}
        data-testid={`${testId}-cta`}
      >
        {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : ctaLabel}
      </Button>
      <ul className="mt-6 space-y-2.5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-gray-200">
            <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
        {disabledFeatures.map((f) => (
          <li key={f} className="flex items-start gap-2 text-gray-500">
            <X className="w-4 h-4 text-gray-600 shrink-0 mt-0.5" />
            <span className="line-through decoration-gray-600/60">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Premium;
